import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useSongStore } from '../store/useSongStore'
import { SectionBlock } from '../components/songbook/SectionBlock'
import { ActionToolbar } from '../components/songbook/ActionToolbar'
import { MiniEditor } from '../components/songbook/MiniEditor'
import { OnlineIndicator } from '../components/offline/OnlineIndicator'
import { AudioCachePrompt } from '../components/offline/AudioCachePrompt'
import { ListenCachePrompt } from '../components/offline/ListenCachePrompt'
import { openAttachment } from '../lib/offline/audioPlayback'
import { useCachedAudioSrc } from '../lib/offline/listenCache'
import type { SectionGroup } from '../types/songbook'

export function SongEditorPage() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentSong,
    isLoading,
    isSaving,
    saveRetries,
    error,
    offlineInfo,
    loadSong,
    updateTitle,
    updateInfo,
    updateListenUrl,
    removeElement,
    moveSectionBlock,
    updateElement,
    uploadAttachment,
    deleteAttachment,
    toggleShareSong,
    clearCurrentSong,
    deleteSong,
    updateDuration,
  } = useSongStore()

  const collections = useSongStore((s) => s.collections)
  const [activePanel, setActivePanel] = useState<'info' | 'listen' | 'files' | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [shareMsg, setShareMsg] = useState<string | null>(null)
  const [editingUrl, setEditingUrl] = useState(false)
  const [printInfo, setPrintInfo] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function handleDelete() {
    if (!currentSong) return
    const target = currentSong.collectionId ? `/songs/collection/${currentSong.collectionId}` : '/songs'
    await deleteSong(currentSong.id)
    navigate(target)
  }

  const cachedListenSrc = useCachedAudioSrc(currentSong?.listenUrl ?? '')

  useEffect(() => {
    if (id) loadSong(id)
    return () => clearCurrentSong()
  }, [id, loadSong, clearCurrentSong])

  // Group elements: Section → Instrument tracks → Chords/Tabs
  const sectionGroups = useMemo((): SectionGroup[] => {
    if (!currentSong) return []

    const groups: SectionGroup[] = []

    for (const el of currentSong.elements) {
      if (el.type === 'section') {
        groups.push({ section: el, tracks: [] })
      } else if (el.type === 'instrument' && groups.length > 0) {
        groups[groups.length - 1].tracks.push({ instrument: el, children: [] })
      } else if ((el.type === 'chord' || el.type === 'tab' || el.type === 'tablature' || el.type === 'image' || el.type === 'midi') && groups.length > 0) {
        const currentGroup = groups[groups.length - 1]
        if (currentGroup.tracks.length > 0) {
          // Add to last instrument track
          currentGroup.tracks[currentGroup.tracks.length - 1].children.push(el)
        } else {
          // No instrument yet — create an implicit "default" track
          currentGroup.tracks.push({
            instrument: {
              id: '_default_' + currentGroup.section.id,
              type: 'instrument',
              instrumentTypeId: '_default',
              number: 0,
            },
            children: [el],
          })
        }
      }
    }

    return groups
  }, [currentSong])

  if (isLoading || !currentSong) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-muted">{t('general.loading')}</p>
      </div>
    )
  }

  const togglePanel = (panel: 'info' | 'listen' | 'files') => {
    setActivePanel(activePanel === panel ? null : panel)
  }

  // Strip HTML so an "empty" rich-text editor (<p></p>, <br>) doesn't
  // count as content. Drives the green "has content" button state.
  const hasInfo = !!currentSong.info.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  const hasListenUrl = !!currentSong.listenUrl?.trim()

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3">
        <button
          className="shrink-0 rounded p-1.5 text-text-muted hover:text-text"
          onClick={() => navigate(currentSong.collectionId ? `/songs/collection/${currentSong.collectionId}` : '/songs')}
          title={t('songbook.back')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <input
          type="text"
          value={currentSong.title}
          onChange={(e) => updateTitle(e.target.value)}
          placeholder={t('songbook.songTitle')}
          className="min-w-0 flex-1 bg-transparent text-lg font-bold text-text placeholder-text-muted focus:outline-none"
        />
        <span className={`shrink-0 text-xs ${saveRetries > 0 ? 'animate-pulse text-error font-bold' : error ? 'text-error' : isSaving ? 'text-yellow-400' : 'text-success'}`}>
          {saveRetries > 0
            ? `⚠ Retry ${saveRetries}...`
            : isSaving
              ? '⏳'
              : '✓'
          }
        </span>
        <button
          className="shrink-0 rounded p-1.5 text-text-muted hover:text-error"
          title={t('songbook.deleteSong')}
          onClick={() => setConfirmDelete(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
        </button>
        <OnlineIndicator />
      </header>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
          <div className="w-full max-w-sm rounded-xl bg-surface p-6 shadow-xl">
            <p className="mb-1 font-semibold text-text">{t('songbook.deleteSong')}</p>
            <p className="mb-6 text-sm text-text-muted">
              "{currentSong.title}" {t('songbook.deleteConfirm')}
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 rounded-lg border border-surface-lighter py-2 text-sm text-text-muted hover:bg-surface-lighter"
                onClick={() => setConfirmDelete(false)}
              >
                {t('general.cancel')}
              </button>
              <button
                className="flex-1 rounded-lg bg-error py-2 text-sm font-semibold text-white hover:opacity-90"
                onClick={handleDelete}
              >
                {t('general.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {offlineInfo && (
        <div className="mx-4 mb-2 rounded bg-surface-lighter px-3 py-1 text-xs text-text-muted">
          {offlineInfo}
        </div>
      )}

      <div className="px-4">
        <AudioCachePrompt song={currentSong} />
      </div>

      {/* Info / Listen toggle buttons */}
      <div className="flex flex-wrap gap-2 px-4 pb-2">
        {/* Preview/Edit toggle */}
        <button
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            previewMode
              ? 'bg-green-600 text-white'
              : 'border border-surface-lighter text-text-muted active:bg-surface-lighter'
          }`}
          onClick={() => setPreviewMode(!previewMode)}
        >
          {previewMode ? (
            <><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>{t('songbook.preview')}</>
          ) : (
            <><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>{t('songbook.edit')}</>
          )}
        </button>

        <button
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            activePanel === 'info'
              ? 'bg-primary text-white'
              : hasInfo
                ? 'bg-success/20 text-success'
                : 'border border-surface-lighter text-text-muted active:bg-surface-lighter'
          }`}
          onClick={() => togglePanel('info')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
          {t('songbook.songInfo')}
        </button>
        <button
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            activePanel === 'listen'
              ? 'bg-primary text-white'
              : hasListenUrl
                ? 'bg-success/20 text-success'
                : 'border border-surface-lighter text-text-muted active:bg-surface-lighter'
          }`}
          onClick={() => togglePanel('listen')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          {t('songbook.listen')}
        </button>

        {/* Share song button */}
        <button
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            currentSong.shareToken
              ? 'bg-success/20 text-success'
              : 'border border-surface-lighter text-text-muted active:bg-surface-lighter'
          }`}
          onClick={async () => {
            const token = await toggleShareSong(currentSong.id)
            if (token) {
              const url = `${window.location.origin}/share/song/${token}`
              await navigator.clipboard.writeText(url)
              setShareMsg(t('songbook.shareCopied'))
              setTimeout(() => setShareMsg(null), 3000)
            }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          {t('songbook.share')}
        </button>

        {/* PDF export button */}
        <button
          className="flex items-center gap-1.5 rounded-lg border border-surface-lighter px-3 py-1.5 text-xs font-medium text-text-muted active:bg-surface-lighter"
          onClick={() => {
            const col = collections.find((c) => c.id === currentSong.collectionId)
            const artist = col?.artist || ''
            const title = currentSong.title || t('songbook.untitled')
            const pdfName = artist ? `${artist} - ${title}` : title
            document.title = pdfName
            window.print()
            document.title = 'Songbook'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          PDF
        </button>

        {/* Files button */}
        <button
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            activePanel === 'files'
              ? 'bg-primary text-white'
              : 'border border-surface-lighter text-text-muted active:bg-surface-lighter'
          }`}
          onClick={() => togglePanel('files')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          {t('songbook.files')}{currentSong.attachments.length > 0 ? ` (${currentSong.attachments.length})` : ''}
        </button>

        {/* Print info checkbox */}
        <label className="flex items-center gap-1 text-xs text-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={printInfo}
            onChange={(e) => setPrintInfo(e.target.checked)}
            className="accent-primary"
          />
          +Info
        </label>
      </div>

      {/* Share confirmation */}
      {shareMsg && (
        <div className="mx-4 mb-2 rounded-lg bg-success/20 px-3 py-1.5 text-center text-xs text-success">
          {shareMsg}
        </div>
      )}

      {/* Info panel */}
      {activePanel === 'info' && (
        <div className="px-4 pb-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="shrink-0 text-xs text-text-muted">{t('songbook.duration')}</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="99"
                value={currentSong.duration != null ? Math.floor(currentSong.duration / 60) : ''}
                onChange={(e) => {
                  const mins = parseInt(e.target.value) || 0
                  const secs = currentSong.duration != null ? currentSong.duration % 60 : 0
                  updateDuration(mins * 60 + secs)
                }}
                placeholder="0"
                className="w-12 rounded border border-surface-lighter bg-surface px-2 py-1 text-center text-sm text-text focus:border-primary focus:outline-none"
              />
              <span className="text-text-muted">:</span>
              <input
                type="number"
                min="0"
                max="59"
                value={currentSong.duration != null ? String(currentSong.duration % 60).padStart(2, '0') : ''}
                onChange={(e) => {
                  const secs = Math.min(59, parseInt(e.target.value) || 0)
                  const mins = currentSong.duration != null ? Math.floor(currentSong.duration / 60) : 0
                  updateDuration(mins * 60 + secs)
                }}
                placeholder="00"
                className="w-12 rounded border border-surface-lighter bg-surface px-2 py-1 text-center text-sm text-text focus:border-primary focus:outline-none"
              />
              <span className="text-xs text-text-muted">min : sek</span>
              {currentSong.duration != null && (
                <button
                  className="text-xs text-text-muted hover:text-error"
                  onClick={() => updateDuration(null)}
                  title="Dauer entfernen"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <MiniEditor
            value={currentSong.info}
            onChange={updateInfo}
            placeholder={t('songbook.songInfoPlaceholder')}
          />
        </div>
      )}

      {/* Listen panel */}
      {activePanel === 'listen' && (
        <div className="px-4 pb-3">
          {/* URL input */}
          {(editingUrl || !currentSong.listenUrl) ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={currentSong.listenUrl}
                onChange={(e) => updateListenUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingUrl(false)}
                placeholder={t('songbook.listenPlaceholder')}
                className="flex-1 rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2 text-sm text-text placeholder-text-muted focus:border-primary focus:outline-none"
                autoFocus
              />
              <button
                className="shrink-0 rounded-lg bg-primary px-3 text-sm font-medium text-white active:bg-primary-dark disabled:opacity-50"
                onClick={() => {
                  if (currentSong.listenUrl && !isAudioUrl(currentSong.listenUrl)) {
                    alert(t('songbook.listenInvalidUrl'))
                    return
                  }
                  setEditingUrl(false)
                }}
              >
                OK
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Audio player */}
              <audio
                src={cachedListenSrc}
                controls
                className="w-full rounded-lg"
                style={{ height: '40px' }}
              />
              <ListenCachePrompt url={currentSong.listenUrl} />
              {/* Edit URL button */}
              <div className="flex items-center gap-2">
                <p className="flex-1 truncate text-xs text-text-muted">{currentSong.listenUrl}</p>
                <button
                  className="shrink-0 rounded p-1 text-text-muted hover:text-text"
                  onClick={() => setEditingUrl(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Files panel */}
      {activePanel === 'files' && (
        <div className="px-4 pb-3">
          {/* Upload button */}
          <button
            className="mb-3 w-full rounded-lg border-2 border-dashed border-surface-lighter py-3 text-center text-sm text-text-muted active:border-primary active:text-primary"
            onClick={() => fileInputRef.current?.click()}
          >
            + {t('songbook.addFile')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.rtf,.txt,.mid,.midi,.mp3,.ogg,.wav,.flac,.aac,.m4a"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) await uploadAttachment(file)
              if (fileInputRef.current) fileInputRef.current.value = ''
            }}
            className="hidden"
          />

          {/* File list */}
          {currentSong.attachments.length === 0 ? (
            <p className="text-center text-xs text-text-muted">{t('songbook.noFiles')}</p>
          ) : (
            <div className="space-y-2">
              {currentSong.attachments.map((att) => {
                const isAudio = /\.(mp3|ogg|wav|flac|aac|m4a)$/i.test(att.filename)
                return (
                  <div key={att.id} className="flex items-center gap-2 rounded-lg border border-surface-lighter bg-surface p-2">
                    <span className="text-sm">
                      {isAudio ? '🎵' : /\.mid/i.test(att.filename) ? '🎹' : '📄'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-text">{att.filename}</p>
                      <p className="text-[10px] text-text-muted">{formatFileSize(att.size)}</p>
                    </div>
                    {/* Download */}
                    <button
                      className="shrink-0 rounded p-1 text-text-muted hover:text-primary"
                      onClick={() => openAttachment(att.storagePath)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    {/* Delete */}
                    <button
                      className="shrink-0 rounded p-1 text-text-muted hover:text-error"
                      onClick={() => {
                        if (confirm(t('songbook.fileDeleteConfirm'))) {
                          deleteAttachment(att.id)
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Print-only header: Song title + artist */}
      {(() => {
        const col = collections.find((c) => c.id === currentSong.collectionId)
        return (
          <div className="hidden print:block px-4 pb-4">
            <h1 className="text-2xl font-bold">{currentSong.title || t('songbook.untitled')}</h1>
            {col?.artist && <p className="text-sm text-gray-500">{col.artist}</p>}
            {printInfo && currentSong.info && (
              <div className="mini-editor mt-2 text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: currentSong.info }} />
            )}
          </div>
        )
      })()}

      {/* Section blocks */}
      <main className="flex-1 overflow-y-auto space-y-2 px-4 pb-24">
        {sectionGroups.map((group, idx) => (
          <SectionBlock
            key={group.section.id}
            section={group.section}
            tracks={group.tracks}
            isActive={activeSectionId === group.section.id}
            readOnly={previewMode}
            onSelect={() => setActiveSectionId(group.section.id)}
            canMoveUp={idx > 0}
            canMoveDown={idx < sectionGroups.length - 1}
            onDeleteElement={removeElement}
            onMoveSection={moveSectionBlock}
            onUpdateElement={(id, data) => updateElement(id, data)}
            onUpdateDots={(id, dots) => updateElement(id, { dots })}
            onUpdateColumns={(id, columns) => updateElement(id, { columns })}
          />
        ))}
      </main>

      {/* Action toolbar (hidden in preview mode) */}
      {!previewMode && <ActionToolbar activeSectionId={activeSectionId} />}
    </div>
  )
}

const AUDIO_EXTENSIONS = ['.mp3', '.ogg', '.wav', '.flac', '.aac', '.m4a', '.wma', '.opus', '.webm']

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function isAudioUrl(url: string): boolean {
  if (!url) return true
  try {
    const pathname = new URL(url).pathname.toLowerCase()
    return AUDIO_EXTENSIONS.some((ext) => pathname.endsWith(ext))
  } catch {
    return false
  }
}
